import { TFunction } from 'i18next'
import { boolean, mixed, number, object, SchemaOf, string } from 'yup'

import { shouldAllowVatNumberWhenExempt } from '@features/invoices/utils/shouldAllowVatNumberWhenExempt'
import { MAX_COMPANY_NAME_LENGTH } from '@features/invoicingSettings/constants/maxCompanyNameLength'
import { FrenchLegalStatus } from '@features/invoicingSettings/enums/frenchLegalStatus'

import { AutoCompletedFields } from '../../../../../../../types/formWithAutoCompletedFields'
import { getRegistrationNumberRegex } from '../../../../../../../utils'
import { getVatNoRegex } from '../../../../../../../utils/getVatNoRegex'

interface OrganizationInfoFormBasic {
  addressLine2?: string | null
  city: string | undefined
  countryId: string | undefined
  email: string | undefined
  fax: string | undefined
  isSiretPendingAssignment?: boolean | undefined
  isVatExempted: boolean | undefined
  legalStatusCustom?: string
  name: string
  phone: string | undefined
  registrationNo: string | undefined
  sharedCapital?: number
  street: string | undefined
  vatNo?: string | null
  zipcode: string | undefined
}

export interface OrganizationInfoForm extends OrganizationInfoFormBasic {
  autoCompletedFields?: AutoCompletedFields<OrganizationInfoFormBasic>
  legalStatusOption?: FrenchLegalStatus
}

export const defaultValues: OrganizationInfoForm = {
  addressLine2: '',
  autoCompletedFields: undefined,
  city: '',
  countryId: '',
  email: '',
  fax: '',
  isSiretPendingAssignment: false,
  isVatExempted: false,
  legalStatusCustom: '',
  legalStatusOption: undefined,
  name: '',
  phone: '',
  registrationNo: '',
  street: '',
  vatNo: '',
  zipcode: '',
  sharedCapital: undefined,
}

export type OrganizationInfoFormSchema = SchemaOf<OrganizationInfoForm>

export const getValidationSchema = (t: TFunction, countryId = 'DK'): OrganizationInfoFormSchema =>
  object({
    addressLine2: string().nullable(),
    autoCompletedFields: object(),
    city: string(),
    countryId: string(),
    email: string().email(),
    fax: string(),
    isSiretPendingAssignment: boolean(),
    isVatExempted: boolean(),
    legalStatusCustom: string()
      .optional()
      .when('legalStatusOption', {
        is: FrenchLegalStatus.Other,
        then: string().required(t('settings.organization.organization_info.basic_info.legal_status_custom.error')),
      }),
    legalStatusOption: mixed().oneOf(Object.values(FrenchLegalStatus)),
    name: string()
      .required()
      .max(
        MAX_COMPANY_NAME_LENGTH,
        t('settings.organization.organization_info.basic_info.name.error.max_length', {
          count: MAX_COMPANY_NAME_LENGTH,
        }),
      ),
    phone: string(),
    registrationNo: string().matches(getRegistrationNumberRegex(countryId), {
      message: t(`settings.organization.organization_info.basic_info.registration_number.org_${countryId}.error`),
      excludeEmptyString: true,
    }),
    sharedCapital: number().optional(),
    street: string(),
    vatNo: string()
      .nullable()
      .when('isVatExempted', {
        is: (isVatExempted: boolean) => !isVatExempted || shouldAllowVatNumberWhenExempt(countryId),
        then: string()
          .nullable()
          .matches(getVatNoRegex(countryId), {
            message: t(`settings.organization.organization_info.basic_info.vatNo.org_${countryId}.error`),
            excludeEmptyString: true,
          }),
      }),
    zipcode: string(),
  })
